import { create } from 'zustand';
import * as Sentry from '@sentry/react';
import { supabase, reportSupabaseError } from '@/lib/supabase';
import type { PracticeTest, PracticeTestAttempt, SectionFilter } from '@/types';

interface PracticeTestState {
  tests: PracticeTest[];
  currentTest: PracticeTest | null;
  currentAnswers: number[];
  currentQuestionIndex: number;
  flaggedQuestions: Set<number>;
  startTime: Date | null;
  attempts: PracticeTestAttempt[];
  isLoading: boolean;
  error: string | null;
  setTests: (tests: PracticeTest[]) => void;
  setCurrentTest: (test: PracticeTest | null) => void;
  answerQuestion: (index: number, answer: number) => void;
  toggleFlag: (index: number) => void;
  goToQuestion: (index: number) => void;
  nextQuestion: () => void;
  previousQuestion: () => void;
  startAttempt: (test: PracticeTest) => void;
  fetchTests: (userId: string) => Promise<void>;
  generateTest: (userId: string, documentIds: string[], difficulty: string, timeLimit: number | null, questionCount: number, sectionFilters?: SectionFilter[]) => Promise<PracticeTest | null>;
  submitAttempt: (userId: string) => Promise<PracticeTestAttempt | null>;
  fetchAttempts: (testId: string) => Promise<void>;
  deleteTest: (testId: string) => Promise<void>;
}

export const usePracticeTestStore = create<PracticeTestState>((set, get) => ({
  tests: [],
  currentTest: null,
  currentAnswers: [],
  currentQuestionIndex: 0,
  flaggedQuestions: new Set(),
  startTime: null,
  attempts: [],
  isLoading: false,
  error: null,

  setTests: (tests) => set({ tests }),
  setCurrentTest: (test) => set({
    currentTest: test,
    currentAnswers: test ? Array(test.questions.length).fill(-1) : [],
    currentQuestionIndex: 0,
    flaggedQuestions: new Set(),
    startTime: null,
  }),
  answerQuestion: (index, answer) => set((state) => {
    const newAnswers = [...state.currentAnswers];
    newAnswers[index] = answer;
    return { currentAnswers: newAnswers };
  }),
  toggleFlag: (index) => set((state) => {
    const newFlags = new Set(state.flaggedQuestions);
    if (newFlags.has(index)) {
      newFlags.delete(index);
    } else {
      newFlags.add(index);
    }
    return { flaggedQuestions: newFlags };
  }),
  goToQuestion: (index) => set((state) => ({
    currentQuestionIndex: Math.max(0, Math.min(index, (state.currentTest?.questions.length || 1) - 1)),
  })),
  nextQuestion: () => set((state) => ({
    currentQuestionIndex: Math.min(
      state.currentQuestionIndex + 1,
      (state.currentTest?.questions.length || 1) - 1
    ),
  })),
  previousQuestion: () => set((state) => ({
    currentQuestionIndex: Math.max(state.currentQuestionIndex - 1, 0),
  })),
  startAttempt: (test) => set({
    currentTest: test,
    currentAnswers: Array(test.questions.length).fill(-1),
    currentQuestionIndex: 0,
    flaggedQuestions: new Set(),
    startTime: new Date(),
  }),

  fetchTests: async (userId: string) => {
    set({ isLoading: true, error: null });
    const { data, error } = await supabase
      .from('practice_tests')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      reportSupabaseError(error, 'fetchTests');
      set({ error: error.message, isLoading: false });
      return;
    }

    set({ tests: data || [], isLoading: false });
  },

  generateTest: async (userId: string, documentIds: string[], difficulty: string, timeLimit: number | null, questionCount: number, sectionFilters?: SectionFilter[]) => {
    return Sentry.startSpan(
      { name: 'practiceTest.generate', op: 'ai.generation', attributes: { 'generation.questionCount': questionCount, 'documents.count': documentIds.length } },
      async () => {
    set({ isLoading: true, error: null });

    // 1. Create practice test row with status: 'generating'
    const { data: newTest, error: insertError } = await supabase
      .from('practice_tests')
      .insert({
        user_id: userId,
        title: 'Generating practice test...',
        document_ids: documentIds,
        questions: [],
        difficulty,
        time_limit_minutes: timeLimit,
        passing_score: 70,
        status: 'generating',
      })
      .select()
      .single();

    if (insertError || !newTest) {
      set({ error: insertError?.message || 'Failed to create practice test', isLoading: false });
      return null;
    }

    // 2. Add to local state immediately
    set((state) => ({
      tests: [newTest, ...state.tests],
      isLoading: false,
    }));

    // 3. Fire-and-forget edge function
    supabase.functions.invoke('generate-practice-test', {
      body: { testId: newTest.id, documentIds, difficulty, timeLimit, questionCount, sectionFilters },
    }).catch((err: unknown) => {
      Sentry.captureException(err, { tags: { context: 'practiceTestStore.generateTest' } });
    });

    return newTest;
      });
  },

  submitAttempt: async (userId: string) => {
    const { currentTest, currentAnswers, startTime } = get();
    if (!currentTest || !startTime) return null;

    const timeTaken = Math.floor((new Date().getTime() - startTime.getTime()) / 1000);

    // Calculate score
    let correct = 0;
    currentTest.questions.forEach((q, i) => {
      if (q.type === 'multiple_choice') {
        if (currentAnswers[i] === q.correct_index) correct++;
      } else if (q.type === 'true_false') {
        const userAnswer = currentAnswers[i] === 1;
        if (userAnswer === q.correct_answer) correct++;
      }
    });
    const score = Math.round((correct / currentTest.questions.length) * 100);
    const passed = score >= currentTest.passing_score;

    const attempt = {
      test_id: currentTest.id,
      user_id: userId,
      answers: currentAnswers,
      score,
      time_taken_seconds: timeTaken,
      passed,
    };

    const { data, error } = await supabase
      .from('practice_test_attempts')
      .insert(attempt)
      .select()
      .single();

    if (error) {
      reportSupabaseError(error, 'practiceTestStore.submitAttempt');
      set({ error: error.message });
      return null;
    }

    set((state) => ({
      attempts: [data, ...state.attempts],
    }));

    return data;
  },

  fetchAttempts: async (testId: string) => {
    const { data, error } = await supabase
      .from('practice_test_attempts')
      .select('*')
      .eq('test_id', testId)
      .order('completed_at', { ascending: false });

    if (error) {
      reportSupabaseError(error, 'practiceTestStore.fetchAttempts');
      set({ error: error.message });
      return;
    }

    set({ attempts: data || [] });
  },

  deleteTest: async (testId: string) => {
    const { error } = await supabase
      .from('practice_tests')
      .delete()
      .eq('id', testId);

    if (error) {
      reportSupabaseError(error, 'practiceTestStore.deleteTest');
      set({ error: error.message });
      return;
    }

    set((state) => ({
      tests: state.tests.filter((t) => t.id !== testId),
      currentTest: state.currentTest?.id === testId ? null : state.currentTest,
    }));
  },
}));
